import { css, html, TemplateResult } from 'lit'
import { property } from 'lit/decorators/property.js'
import '@pushengineering/push-web-components/text'
import '@pushengineering/push-web-components/button'
import '@pushengineering/push-web-components/vertical-stack'
import '@pushengineering/push-web-components/link'
import '@pushengineering/push-web-components/image'

import CirkayElement from '../../internal/CirkayElement'
import { global } from '../../theme/global'
import { LogoSet } from '../login/types'

export class Frame extends CirkayElement {
    /** Image set for the logo  */
    @property({ type: Object })
    logoSet: LogoSet = {
        large: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        medium: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        small: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        alt: 'Cirkay Logo',
    }

    /** Title of the Fan Pass if you dont want it called Fan Pass  */
    @property({ type: String })
    fanPassTitle?: string | undefined = 'Fan Pass'

    /** Max amount of pages in the flow  */
    @property({ type: Number })
    maxPages?: number | undefined = undefined

    /** The index number of the current page of the flow  */
    @property({ type: Number })
    currentPageNumber?: number | undefined = undefined

    /** The url of the logo  */
    @property({ type: String })
    logoUrl: string = 'https://cirkay.com/'

    constructor() {
        super()
    }

    connectedCallback() {
        super.connectedCallback()
        //TODO: Add Validtion for logoSet
    }

    /**
     * renderLogo - Renders the logo for the login component
     *
     * @return  {TemplateResult}
     */
    private renderLogo(): TemplateResult {
        if (this.logoSet) {
            return html`
                <h1 part="brand-text"
                    class="logo logo--title-hidden"
                    style="--pu-size-logo-height:5em;"
                >
                    <a href="${this.logoUrl}" target="_blank">
                        <pu-image
                            exportparts="image:brand-image"
                            part="base-brand"
                            class="logo__image"
                            size="contain"
                            imgsizes=""
                            imgAlt=${this.logoSet.alt}
                            src=${this.logoSet.large.src}
                            width="24"
                            height="24"
                            srcSet=${`${this.logoSet.large.src} 4x,
                                ${this.logoSet.large.src} 3x,
                                ${this.logoSet.medium.src} 2x,
                                ${this.logoSet.small.src} 1x
                            `}
                            lazy="lazy"
                        >
                        </pu-image>
                        <span class=" logo-title visually-hidden">
                            ${this.fanPassTitle || 'Fan Pass'}
                        </span>
                    </a>
                </h1>
            `
        } 
        return this.renderCirkayLogo()
    }

    renderCirkayLogo(): TemplateResult {
        return html`
            <h1 part="logo"
                class="logo"
                style="--pu-size-logo-height:5em;"
            >
                <a href="${this.logoUrl}" target="_blank">
                    <pu-image
                        part="logo-image"
                        class="logo__image"
                        size="contain"
                        imgsizes=""
                        imgAlt="Cirkay Logo"
                        src="https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp"
                        width="24"
                        height="24"
                        srcSet=${`https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 4x,
                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 3x,
                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 2x,
                            https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 1x
                        `}
                        lazy="lazy"
                    >
                    </pu-image>
                    <span class="logo-title visually-hidden ">
                        ${this.fanPassTitle || 'Fan Pass'}
                    </span>
                </a>
            </h1>
        `
    }

    /**
     * renderPrivacyLinks - Renders the privacy and terms links for the login component
     *
     * @return  {TemplateResult}
     */
    private renderPrivacyLinks(): TemplateResult {
        return html`
            <pu-vertical-stack
                exportparts="base:legal-layout"
                part="base-legal-layout"
                spacing="32"
                align="center"
                alignNarrow="center"
                >
                <pu-vertical-stack
                    exportparts="base:legal-links-layout"
                    part="base-legal-links-layout"
                    align="center"
                    alignNarrow="center"
                    spacing="0"
                    spacingNarrow="0"
                >
                    <pu-link
                        class="legal-link-text"
                        url="https://cirkay.com/terms/"
                        target="_blank"
                    >
                        <pu-text
                            as="span"
                            variant="bodyXs"
                            fontWeight="regular"
                            withoutMargin
                            spacing="normal"
                            lineHeight="dense"
                        >
                            Terms of Use
                        </pu-text>
                    </pu-link>
                    <pu-text
                        class="legal-link-text"
                        lineheight="compact"
                        as="span"
                        variant="bodyXs"
                        withoutMargin
                        fontWeight="bold"
                        lineHeight="dense"
                    >
                        Powered by
                        <pu-link class="legal-link-text" target="_blank" url="http://cirkay.com">Cirkay&reg;</pu-link>
                    </pu-text>

                    <pu-link
                        class="legal-link-text"
                        url="https://cirkay.com/privacy/"
                        target="_blank"
                    >
                        <pu-text
                            as="span"
                            variant="bodyXs"
                            fontWeight="regular"
                            withoutMargin
                            spacing="normal"
                            lineHeight="dense"
                        >
                            Privacy
                        </pu-text>
                    </pu-link>
                </pu-vertical-stack>
                <div part="image-container" class="cirkay-logo image-container image-fit image-fit--contain ">
                    <a href="https://cirkay.com/" target="_blank">
                        <pu-image
                            part="logo-image"
                            class="logo__image cirkay-logo"
                            size="contain"
                            imgAlt="Cirkay Logo"
                            src="https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp"
                            width="12"
                            height="12"
                            imgsizes=""
                            srcSet=${`https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 4x,
                                https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 3x,
                                https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 2x,
                                https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp 1x
                            `}
                            lazy="lazy"
                        >
                        </pu-image>
                    </a>
                </div>
            </pu-vertical-stack>
        `
    }

    /**
     * renderPager - Renders the page counter at the bottom of the flow
     *
     * @return  {TemplateResult}
     */
    private renderPager(pageNumber: Number = 1): TemplateResult {
        if (!this.maxPages) {
            return html``
        }

        const array = new Array(this.maxPages).fill('')

        return html`
            <div part="pager" class="pager">
                ${array.map((_, index) => {
                    return html`
                        <span
                            part="pager-dot"
                            class=${`pagerDot ${pageNumber === index + 1 ? 'active' : ''} ${index}`}
                        ></span>
                    `
                })}
            </div>
        `
    }

    public render(): TemplateResult {
        return html`
            <pu-vertical-stack
                exportparts="base:frame-layout,base:flow-pager"
                part="base-frame-layout base-flow-pager"
                spacing="72"
                spacingNarrow="72"
                align="center"
                alignNarrow="center"
                justify="center"
                justifyNarrow="space-between"
            >
                <pu-vertical-stack
                    exportparts="base:flow-content-layout"
                    part="base-flow-content-layout"
                    class="logo-content-container"
                    spacing="32"
                    spacingNarrow="24"
                    align="center"
                    alignNarrow="center"
                    justify="center"
                    justifyNarrow="center"
                >
                    ${this.renderLogo()}
                    <slot></slot>
                </pu-vertical-stack>
                <pu-vertical-stack
                    exportparts="base:flow-footer-layout"
                    part="base-flow-footer-layout"
                    spacing="32"
                    spacingNarrow="24"
                    align="center"
                    alignNarrow="center"
                    justify="center"
                    justifyNarrow="center"
                >
                    ${this.renderPrivacyLinks()}
                    ${this.renderPager(this.currentPageNumber)}
                </pu-vertical-stack>
            </pu-vertical-stack>
        `
    }

    static styles = [
        global,
        css`
            :host {
                font-size: var(--fp-font-size-base, 1em);
            }
            .group--divider :last-child {
                margin-right: var(--pu-space-600);
            }

            .group--divider :not(:last-child) {
                position: relative;
                padding-right: var(--pu-space-base-gap, 0.75em);
            }

            .group--divider :not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateX(50%) translateY(-50%);
                width: 0.2em;
                height: var(--pu-text-body-size-lg, 1.25em);
                display: inline-block;
                background-color: var(--pu-color-divider, rgb(214, 214, 214));
                margin: auto;
                border-radius: var(--pu-border-radius-full, 624.9375em);
            }

            .logo-content-container {
                flex: 1;
                padding: 0 1.5em;
            }

            .cirkay-logo {
                width: 24px;
            }

            .legal-link-text {
                line-height: 1;
            }

            .pager {
                display: flex;
                flex-direction: row;
                gap: 1em;
            }

            .pagerDot {
                height: 1em;
                width: 1em;
                background-color: rgb(214, 214, 214);
                border-radius: 50px;
            }

            .pagerDot.active {
                background-color: rgb(136, 136, 136);
            }

            .logo.logo--title-hidden {
                font-size: 16px;
            }
            
            .logo {
                display: flex;
                align-items: center;
                color: inherit;
                margin: 0;
                height: var(--pu-size-logo-height);
                line-height: 1;
            }
            .logo a {
                display: block;
                color: inherit;
                text-decoration: none;
                height: 100%;
            }
            .logo-title:not(.visually-hidden) {
                display: inline-block;
                font-family: var(--pu-text-logo-family, var(--pu-text-heading-family, 'Roboto Serif', Times, 'Times New Roman', Georgia, serif));
                font-size: var(--pu-font-logo-size, 1.75em);
            }

            .visually-hidden{
                display: none;
            }

            @media only screen and (max-width: 48em) {
                .logo {
                    height: var(--pu-size-mobile-logo-height, calc(var(--pu-size-logo-height, 5em) / 1.25));
                }
            }

            /* Hide image for title */
            .logo--show-title {
                line-height: inherit;
            }
            .logo--show-title .logo__image {
                display: none;
            }
            .logo--show-title a {
                height: auto;
            }
        `,
    ]
}
